import { ExclamationTriangleIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import type { Model, ModelConfig } from '../../types';
import { ACCOUNT_MODELS_SETTINGS_PATH, type AccountModelPicks } from '../../hooks/useAccountModels';

/**
 * Model lifecycle in the model pickers (task 1093): agent forms, the inline
 * agent config editor and node overrides list Model Configs, each carrying its
 * Model's stored status.
 *
 * - `deprecated`: listed with a "deprecated" mark and the shutdown date, and
 *   an amber note under the picker when it is the selected one.
 * - `retired`: hidden, unless it is the value already selected — then listed
 *   disabled with a "retired" label so the picker still shows what is in use.
 *
 * The note also says what happens on the shutdown date (task 1130): the
 * resolver runs a withdrawn Model's calls on its successor (service task
 * 1129), or, with no successor set, sends them unchanged — the warning case.
 */

const day = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : null);

function lifecycleSuffix(config: ModelConfig): string {
  const { status, shutdownDate } = config.model;
  if (status === 'retired') return ' — retired';
  if (status === 'deprecated') return ` — deprecated${day(shutdownDate) ? `, shuts down ${day(shutdownDate)}` : ''}`;
  return '';
}

/** The configs a picker offers: retired Models only when already selected. */
export function pickableModelConfigs(configs: ModelConfig[], selectedId: string | null | undefined): ModelConfig[] {
  return configs.filter((c) => c.model.status !== 'retired' || c.id === selectedId);
}

/**
 * The account's model policy in the pickers (task 1147). A config whose Model
 * the account does not allow stays listed but disabled and greyed, with the
 * reason; a config on the account's default Model for its provider is marked
 * Default. A native `<option>` cannot hold a link, so the reason's link to the
 * Models settings page is `ModelPolicyNote`, under the picker.
 */
export const NOT_ALLOWED_REASON = 'Not allowed in this account · Models settings';

function policySuffix(config: ModelConfig, policy: AccountModelPicks | null | undefined): string {
  if (!policy) return '';
  if (policy.disallowed.has(config.model.id)) return ' — not allowed in this account';
  if (policy.defaults.has(config.model.id)) return ' · Default';
  return '';
}

interface ModelConfigPickerOptionsProps {
  configs: ModelConfig[];
  selectedId: string | null | undefined;
  label: (config: ModelConfig) => string;
  /** The account's model policy (`useAccountModelPicks`); null or absent while unknown. */
  policy?: AccountModelPicks | null;
  /**
   * Configs listed but not selectable, each with why (task 1301: the failover
   * picker lists the agent's primary config disabled, "— the primary").
   */
  unavailable?: { ids: (string | null | undefined)[]; reason: string };
}

/** `<option>`s for a Model Config `<select>`, with the lifecycle and policy marks above. */
export function ModelConfigPickerOptions({ configs, selectedId, label, policy, unavailable }: ModelConfigPickerOptionsProps) {
  return (
    <>
      {pickableModelConfigs(configs, selectedId).map((config) => {
        const notAllowed = policy?.disallowed.has(config.model.id) ?? false;
        const isUnavailable = unavailable?.ids.includes(config.id) ?? false;
        return (
          <option
            key={config.id}
            value={config.id}
            disabled={config.model.status === 'retired' || notAllowed || isUnavailable}
            data-status={config.model.status}
            data-policy={notAllowed ? 'not-allowed' : policy?.defaults.has(config.model.id) ? 'default' : undefined}
            title={notAllowed ? NOT_ALLOWED_REASON : undefined}
            className={notAllowed ? 'text-gray-400' : config.model.status === 'deprecated' ? 'text-amber-700' : undefined}
          >
            {label(config)}
            {lifecycleSuffix(config)}
            {policySuffix(config, policy)}
            {isUnavailable ? ` — ${unavailable!.reason}` : ''}
          </option>
        );
      })}
    </>
  );
}

/**
 * The line under a picker that has configs the account does not allow: the
 * reason, linked to the Models settings page. When the SELECTED config is one
 * of them it says what that means for this agent or node: the service runs it
 * on the account's default Model instead (service task 1146's substitution).
 */
export function ModelPolicyNote({
  configs,
  selectedId,
  policy,
}: {
  configs: ModelConfig[];
  selectedId: string | null | undefined;
  policy: AccountModelPicks | null | undefined;
}) {
  if (!policy) return null;
  const pickable = pickableModelConfigs(configs, selectedId);
  if (!pickable.some((config) => policy.disallowed.has(config.model.id))) return null;
  const selected = selectedId ? pickable.find((config) => config.id === selectedId) : undefined;
  const selectedNotAllowed = selected ? policy.disallowed.has(selected.model.id) : false;

  return (
    <p
      data-testid="model-policy-note"
      className={`mt-1 flex items-center gap-1 text-xs ${
        selectedNotAllowed ? 'text-amber-700 dark:text-amber-300' : 'text-gray-500 dark:text-gray-400'
      }`}
    >
      <NoSymbolIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
      <span>
        {selectedNotAllowed
          ? `${modelName(selected!.model)} is not allowed in this account; its calls run on the account's default Model. `
          : 'Greyed-out configs are not allowed in this account. '}
        <Link
          to={ACCOUNT_MODELS_SETTINGS_PATH}
          className="font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
        >
          Models settings
        </Link>
      </span>
    </p>
  );
}

const modelName = (model: Pick<Model, 'displayName' | 'modelId'>) => model.displayName ?? model.modelId;

/**
 * The lifecycle sentence for a deprecated or retired Model. `models` are the
 * Models the caller has loaded (a picker's configs carry theirs; the Model
 * Config form has the catalog) and name the successor; one not among them is
 * called "its successor Model".
 */
export function modelLifecycleText(model: Model, models: Pick<Model, 'id' | 'displayName' | 'modelId'>[]): string | null {
  if (model.status === 'active') return null;
  const name = modelName(model);
  const found = model.successorModelId ? models.find((m) => m.id === model.successorModelId) : undefined;
  const successor = model.successorModelId ? (found ? modelName(found) : 'its successor Model') : null;
  const shutdown = day(model.shutdownDate);

  if (model.status === 'retired') {
    return successor
      ? `${name} is retired; it runs on ${successor} instead.`
      : `${name} is retired. This config keeps working until the provider refuses requests; choose another config.`;
  }
  if (shutdown) {
    return successor
      ? `${name} is deprecated and shuts down ${shutdown}; it will run on ${successor} after that.`
      : `${name} is deprecated and shuts down ${shutdown}; no successor is set.`;
  }
  return successor
    ? `${name} is deprecated; it will run on ${successor} once it is retired.`
    : `${name} is deprecated; no successor is set.`;
}

/** The amber note under a picker whose selected config runs on a deprecated or retired Model. */
export function ModelLifecycleNote({ configs, selectedId }: { configs: ModelConfig[]; selectedId: string | null | undefined }) {
  const selected = selectedId ? configs.find((c) => c.id === selectedId) : undefined;
  if (!selected) return null;
  const text = modelLifecycleText(selected.model, configs.map((c) => c.model));
  if (!text) return null;
  return (
    <p data-testid="model-lifecycle-note" className="mt-1 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
      <ExclamationTriangleIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
      {text}
    </p>
  );
}
